import { useEffect, useMemo, useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { QIconButton } from '@kw/quokka-ui';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useFetchService } from '../../../utils/fetchService';
import { httpRequest } from '../../../utils/network.service';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { CountryPickerModal } from './CountryPickerModal';
import { CountryCatalogEntry, createCountryOptions, normalizeCountryName } from './countrySettings';
import { OrganizationSettingsDialog } from './OrganizationSettingsDialog';
import { RiskyNationsPanel } from './RiskyNationsPanel';
import { getConfirmationCopy, getLatestRiskyNationUpdate, isHomeCountryConflict, sortRiskyNations } from './RiskyNations.utils';
import {
  ConfirmationAction,
  HighRiskCountriesResponse,
  HighRiskCountry,
  HomeCountryConflict,
  HomeCountryConflictModal,
  SortDirection,
  SortField,
} from './RiskyNations.types';

interface RiskyNationsCardProps {
  isAdmin: boolean;
  homeCountryCode?: string;
}

const REEVALUATION_MESSAGE = 'Please allow a few minutes to re-evaluate analysis and enabled Policies.';
const HIGH_RISK_COUNTRIES_PATH = '/list/high-risk-countries';

const getDisabledReason = (countryCode: string, homeCountryCode: string | undefined, configuredCountryCodes: Set<string>) => {
  if (countryCode === homeCountryCode) return 'Home country';
  if (configuredCountryCodes.has(countryCode)) return 'On risky nations list';
  return undefined;
};

const getErrorMessage = (data: unknown, fallbackMessage: string) => {
  if (!data || typeof data !== 'object' || !('message' in data)) return fallbackMessage;

  const { message } = data as { message?: unknown };
  return typeof message === 'string' ? message : fallbackMessage;
};

const getMutationRequest = (action: ConfirmationAction): { path: string; method: 'POST' | 'DELETE' } => {
  const basePath = `${process.env.LIST_SERVICE_URL}${HIGH_RISK_COUNTRIES_PATH}`;

  if (action.kind === 'remove') {
    return { path: `${basePath}/${encodeURIComponent(action.country.countryCode)}`, method: 'DELETE' };
  }

  if (action.kind === 'restore') {
    return { path: `${basePath}/restore-defaults`, method: 'POST' };
  }

  return { path: basePath, method: 'DELETE' };
};

const RiskyNationsSuccessToast = ({ title }: { title: string }) => (
  <ToastContent>
    <ToastTitle>{title}</ToastTitle>
    <ToastDescription>{REEVALUATION_MESSAGE}</ToastDescription>
  </ToastContent>
);

const renderToastDismissAction = (onDismiss: (id: SnackbarKey) => void, snackbarId: SnackbarKey) => (
  <DismissButton aria-label="Dismiss notification" size="small" onClick={() => onDismiss(snackbarId)}>
    <Close fontSize="small" />
  </DismissButton>
);

export const RiskyNationsCard = ({ isAdmin, homeCountryCode }: RiskyNationsCardProps) => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    data: riskyNationsData,
    error: riskyNationsError,
    mutate: mutateRiskyNations,
  } = useFetchService<HighRiskCountriesResponse>(HIGH_RISK_COUNTRIES_PATH, accessToken);
  const { data: countriesData, error: countriesError } = useFetchService<CountryCatalogEntry[]>('/list/countryCodes', accessToken);

  const [sortField, setSortField] = useState<SortField>('country');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingCountryCode, setPendingCountryCode] = useState<string | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction | null>(null);
  const [homeCountryConflict, setHomeCountryConflict] = useState<HomeCountryConflictModal | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [knownDefaultState, setKnownDefaultState] = useState<boolean | undefined>(riskyNationsData?.isDefault);

  useEffect(() => {
    setKnownDefaultState(riskyNationsData?.isDefault);
  }, [riskyNationsData]);

  const riskyNations = useMemo(() => riskyNationsData?.countries || [], [riskyNationsData?.countries]);
  const configuredCountryCodes = useMemo(
    () => new Set<string>(riskyNations.map(country => country.countryCode.toUpperCase())),
    [riskyNations],
  );
  const defaultHighRiskCountryCodes = useMemo(
    () => new Set((riskyNationsData?.defaultHighRiskCountries || []).map(countryCode => countryCode.toUpperCase())),
    [riskyNationsData?.defaultHighRiskCountries],
  );
  const normalizedHomeCountryCode = homeCountryCode?.toUpperCase();
  const countryOptions = useMemo(() => createCountryOptions(countriesData || []), [countriesData]);
  const pickerOptions = useMemo(
    () =>
      countryOptions.map(option => ({
        ...option,
        disabledReason: getDisabledReason(option.value, normalizedHomeCountryCode, configuredCountryCodes),
      })),
    [configuredCountryCodes, countryOptions, normalizedHomeCountryCode],
  );
  const sortedRiskyNations = useMemo(
    () => sortRiskyNations(riskyNations, sortField, sortDirection),
    [riskyNations, sortDirection, sortField],
  );
  const { ofacCount, customCount } = useMemo(
    () =>
      riskyNations.reduce(
        (counts, country) => ({
          ofacCount: counts.ofacCount + Number(country.source === 'OFAC'),
          customCount: counts.customCount + Number(country.source === 'CUSTOM'),
        }),
        { ofacCount: 0, customCount: 0 },
      ),
    [riskyNations],
  );

  const total = riskyNationsData?.total ?? riskyNations.length;
  const isLoading = !riskyNationsData && !riskyNationsError;
  const hasError = Boolean(riskyNationsError);
  const canAddCountry = Boolean(riskyNationsData && countriesData && !riskyNationsError && !countriesError);
  const latestUpdatedDate = getLatestRiskyNationUpdate(riskyNations);

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(currentDirection => (currentDirection === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }

    setSortField(field);
    setSortDirection('ASC');
  };

  const closeAddModal = () => {
    if (isSaving) return;

    setIsAddModalOpen(false);
    setPendingCountryCode(null);
  };

  const openAddModal = () => {
    setPendingCountryCode(null);
    setIsAddModalOpen(true);
  };

  const showConflict = (conflict: HomeCountryConflict, operation: HomeCountryConflictModal['operation']) => {
    setHomeCountryConflict({
      homeCountryName: conflict.homeCountryName || conflict.homeCountryCode,
      message: conflict.message,
      operation,
    });
  };

  const openRestoreDefaults = () => {
    if (normalizedHomeCountryCode && defaultHighRiskCountryCodes.has(normalizedHomeCountryCode)) {
      const homeCountry = countryOptions.find(country => country.value === normalizedHomeCountryCode);
      setHomeCountryConflict({
        homeCountryName: homeCountry?.label || normalizedHomeCountryCode,
        message: '',
        operation: 'RESTORE_DEFAULTS',
      });
      return;
    }

    setConfirmationAction({ kind: 'restore' });
  };

  const showSuccessToast = (title: string) => {
    enqueueSnackbar(<RiskyNationsSuccessToast title={title} />, {
      variant: 'success',
      action: renderToastDismissAction.bind(null, closeSnackbar),
    });
  };

  const showRequestError = (data: unknown, fallbackMessage: string) => {
    enqueueSnackbar(getErrorMessage(data, fallbackMessage), { variant: 'error' });
  };

  const saveResponse = async (response: HighRiskCountriesResponse, defaultState: boolean) => {
    const isDefault = response.isDefault ?? defaultState;
    setKnownDefaultState(isDefault);
    await mutateRiskyNations(
      {
        ...response,
        defaultHighRiskCountries: response.defaultHighRiskCountries ?? riskyNationsData?.defaultHighRiskCountries,
        isDefault,
      },
      false,
    );
  };

  const addCountry = async () => {
    if (!pendingCountryCode || isSaving) return;

    const selectedCountry = countryOptions.find(country => country.value === pendingCountryCode);
    if (!selectedCountry) return;

    setIsSaving(true);
    const result = await httpRequest({
      path: `${process.env.LIST_SERVICE_URL}${HIGH_RISK_COUNTRIES_PATH}`,
      method: 'POST',
      body: { countryCode: pendingCountryCode },
      accessToken,
    });
    setIsSaving(false);

    if (result.error) {
      if (result.response?.status === 409 && isHomeCountryConflict(result.data)) {
        setIsAddModalOpen(false);
        setPendingCountryCode(null);
        showConflict(result.data, 'ADD');
        return;
      }

      showRequestError(result.data, 'Error adding risky nation.');
      return;
    }

    await saveResponse(result.data as HighRiskCountriesResponse, false);
    closeAddModal();
    showSuccessToast(`${selectedCountry.label} added to risky nations`);
  };

  const confirmMutation = async () => {
    if (!confirmationAction || isSaving) return;

    const action = confirmationAction;
    const request = getMutationRequest(action);

    setIsSaving(true);
    const result = await httpRequest({ ...request, accessToken });
    setIsSaving(false);

    if (result.error) {
      if (action.kind === 'restore' && result.response?.status === 409 && isHomeCountryConflict(result.data)) {
        setConfirmationAction(null);
        showConflict(result.data, 'RESTORE_DEFAULTS');
        return;
      }

      showRequestError(result.data, 'Error updating risky nations.');
      return;
    }

    await saveResponse(result.data as HighRiskCountriesResponse, action.kind === 'restore');
    setConfirmationAction(null);

    if (action.kind === 'remove') showSuccessToast(`${normalizeCountryName(action.country.name)} removed from risky nations`);
    if (action.kind === 'clear') showSuccessToast('Risky nations list cleared');
    if (action.kind === 'restore') showSuccessToast('Risky nations restored to defaults');
  };

  const lastConfirmationCopy = useRef(getConfirmationCopy(null));
  if (confirmationAction) lastConfirmationCopy.current = getConfirmationCopy(confirmationAction);
  const confirmationCopy = lastConfirmationCopy.current;
  const conflictTitle = homeCountryConflict?.operation === 'ADD' ? 'Cannot add country' : 'Cannot restore defaults';
  const conflictBody =
    homeCountryConflict?.operation === 'ADD'
      ? homeCountryConflict.message ||
        `${homeCountryConflict.homeCountryName} cannot be added to Risky Nations because it is the organization's Home Country.`
      : `The default Risky Nations list cannot be restored because your Home Country is set to ${
          homeCountryConflict?.homeCountryName || ''
        }, a current default Risky Nation per OFAC. Update your Home Country or continue building a custom Risky Nations list.`;

  return (
    <>
      <RiskyNationsPanel
        countries={sortedRiskyNations}
        total={total}
        ofacCount={ofacCount}
        customCount={customCount}
        latestUpdatedDate={latestUpdatedDate}
        isAdmin={isAdmin}
        isLoading={isLoading}
        hasError={hasError}
        isSaving={isSaving}
        canAddCountry={canAddCountry}
        restoreDefaultsDisabled={knownDefaultState === true}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={toggleSort}
        onAddCountry={openAddModal}
        onClear={() => setConfirmationAction({ kind: 'clear' })}
        onRestore={openRestoreDefaults}
        onRemove={(country: HighRiskCountry) => setConfirmationAction({ kind: 'remove', country })}
      />

      <CountryPickerModal
        id="add-risky-nations"
        title="Add risky nations"
        open={isAddModalOpen}
        options={pickerOptions}
        selectedValue={pendingCountryCode}
        onSelect={setPendingCountryCode}
        onClose={closeAddModal}
        onSave={addCountry}
        isSaving={isSaving}
        saveDisabled={!pendingCountryCode || isSaving}
      />

      <OrganizationSettingsDialog
        id="risky-nations-confirmation"
        open={Boolean(confirmationAction)}
        title={confirmationCopy.title}
        body={confirmationCopy.body}
        primaryLabel={confirmationCopy.confirmLabel}
        onPrimary={confirmMutation}
        onClose={() => {
          if (!isSaving) setConfirmationAction(null);
        }}
        showCancel
        isSaving={isSaving}
      />

      <OrganizationSettingsDialog
        id="home-country-conflict"
        open={Boolean(homeCountryConflict)}
        title={conflictTitle}
        body={conflictBody}
        primaryLabel="OK"
        onPrimary={() => setHomeCountryConflict(null)}
        onClose={() => setHomeCountryConflict(null)}
      />
    </>
  );
};

const ToastContent = styled('div')`
  color: #ffffff;
`;

const ToastTitle = styled('div')`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
`;

const ToastDescription = styled('div')`
  margin-top: 3px;
  font-size: 12px;
  line-height: 18px;
`;

const DismissButton = styled(QIconButton)`
  color: #ffffff;
`;
